/**
 * Boost AI Filter Integration
 *
 * Custom integration that uses Boost AI's API while maintaining
 * the existing filter UI styling and product card design.
 *
 * Features:
 * - Server-side initial render (SEO friendly)
 * - AJAX filtering via Boost API
 * - Maintains existing filter checkbox styling
 * - Renders products using theme's product card design
 * - Custom pagination for filtered results
 */

(function() {
  'use strict';

  // Configuration - pulled from boostPFSAppConfig set in Liquid
  const BoostFilterIntegration = {
    config: null,
    state: {
      isFiltering: false,
      currentFilters: {},
      currentPage: 1,
      currentSort: 'manual',
      totalProducts: 0,
      isInitialized: false,
      filterVisibilitySet: false
    },

    /**
     * Initialize the integration
     */
    init: function() {
      if (this.state.isInitialized) return;

      // Get config from global boostPFSAppConfig
      if (typeof boostPFSAppConfig === 'undefined') {
        console.warn('BoostFilterIntegration: boostPFSAppConfig not found');
        return;
      }

      this.config = boostPFSAppConfig;
      this.state.isInitialized = true;


      // Parse initial filters from URL
      this.parseUrlFilters();

      // Sync checkbox states with URL filters
      this.syncCheckboxStates();

      // Hide all filters initially until Boost API tells us which are enabled
      // This prevents showing all filters briefly before hiding non-enabled ones
      this.hideAllFiltersInitially();

      // Bind event listeners
      this.bindFilterEvents();
      this.bindSortEvents();
      this.bindPaginationEvents();
      this.bindClearFilterEvents();
      this.bindSidebarToggleEvents();
      this.bindMobileFilterEvents();
      this.bindCollapsibleTriggerEvents();

      // Fetch initial filter tree to determine which filters are enabled
      this.fetchFilterTree();

      // If URL has filters, fetch filtered results
      if (Object.keys(this.state.currentFilters).length > 0 || this.getUrlParam('page') > 1) {
        this.fetchFilteredProducts();
      }

    },

    /**
     * Get the filter configuration mode and custom filters
     */
    getFilterConfig: function() {
      return window.boostFilterConfig || { mode: 'auto', customFilters: {} };
    },

    /**
     * Check if we're in auto mode (no custom filters configured)
     */
    isAutoMode: function() {
      return this.getFilterConfig().mode === 'auto';
    },

    /**
     * Check if we're in search mode (search page vs collection page)
     */
    isSearchMode: function() {
      return window.boostSearchMode === true;
    },

    /**
     * Get the search terms (for search mode)
     */
    getSearchTerms: function() {
      return window.boostSearchTerms || '';
    },

    /**
     * Get the appropriate API URL (filter or search)
     */
    getApiBaseUrl: function() {
      if (this.isSearchMode()) {
        return this.config.api.searchUrl;
      }
      return this.config.api.filterUrl;
    },

    /**
     * Fetch filter tree from Boost API to determine enabled/disabled filters
     */
    fetchFilterTree: function() {
      const params = new URLSearchParams();
      params.set('shop', this.config.shop.domain);
      params.set('build_filter_tree', 'true');
      params.set('page', 1);
      params.set('limit', 1); // We only need the filter tree, not products

      // Search mode: add query param, skip collection_scope
      // Collection mode: add collection_scope
      if (this.isSearchMode()) {
        params.set('q', this.getSearchTerms());
      } else {
        params.set('collection_scope', this.config.general.collection_id);
      }

      const url = `${this.getApiBaseUrl()}?${params.toString()}`;

      fetch(url)
        .then(response => response.json())
        .then(data => {
          if (data.filter) {
            if (this.isAutoMode()) {
              // Auto mode: generate all enabled filters dynamically
              this.renderAutoFilters(data.filter);
            } else {
              // Custom mode: show/hide configured filter blocks
              this.updateFilterVisibility(data.filter);
              this.renderFilterOptions(data.filter);
            }
          }
        })
        .catch(error => {
          console.warn('BoostFilterIntegration: Could not fetch filter tree', error);
        });
    },

    /**
     * Hide all filter groups initially (before Boost API responds)
     * This prevents filters from flashing visible before we know which are enabled
     */
    hideAllFiltersInitially: function() {
      document.querySelectorAll('[data-boost-filter-key]').forEach(group => {
        group.style.display = 'none';
      });
    },

    /**
     * Hide the filter toggle button (when no filters are available)
     */
    hideFilterToggle: function() {
      const filterToggle = document.querySelector('[data-boost-filter-toggle]');
      if (filterToggle) {
        filterToggle.style.display = 'none';
      }
    },

    /**
     * Show the filter toggle button
     */
    showFilterToggle: function() {
      const filterToggle = document.querySelector('[data-boost-filter-toggle]');
      if (filterToggle) {
        filterToggle.style.display = '';
      }
    },

    /**
     * Re-enforce filter visibility using stored enabled filters
     * Called after products render to ensure bundle.js doesn't reset visibility
     */
    enforceFilterVisibility: function() {
      if (!this.state.filterVisibilitySet || !this.enabledFilterKeys) {
        // Initial visibility not set yet, hide all as fallback
        this.hideAllFiltersInitially();
        return;
      }

      document.querySelectorAll('[data-boost-filter-key]').forEach(group => {
        const filterKey = group.dataset.boostFilterKey;
        if (this.enabledFilterKeys.includes(filterKey)) {
          group.style.display = '';
        } else {
          group.style.display = 'none';
        }
      });
    },

    /**
     * Render all enabled filters automatically (auto mode)
     * Used when no custom filter blocks are configured in the theme editor
     */
    renderAutoFilters: function(filterTree) {
      const autoContainer = document.querySelector('[data-boost-auto-filters]');
      if (!autoContainer) return;

      // Parse the filter tree
      let filters = this.parseFilterTree(filterTree);

      // Filter to only active filters with values
      // Boost API can return values in different locations: filter.values or filter.options.values
      const activeFilters = filters.filter(f => {
        if (!f || !f.filterOptionId || f.status !== 'active') return false;
        // Check for values in different locations
        const values = f.values || (f.options && f.options.values) || f.options || [];
        return Array.isArray(values) && values.length > 0;
      });

      if (activeFilters.length === 0) {
        autoContainer.innerHTML = '<p class="text-placehoder text-small py-2.5">No filters available</p>';
        // Hide the filter toggle button if no filters available
        this.hideFilterToggle();
        return;
      }

      // Sort filters for auto mode:
      // 1. Brand/Vendor first
      // 2. Everything else alphabetically by label
      // 3. Colour always last
      activeFilters.sort((a, b) => {
        const aId = a.filterOptionId;
        const bId = b.filterOptionId;
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();

        // Brand/Vendor always first
        const aIsBrand = aId === 'pf_v_brand' || aId === 'pf_v_vendor';
        const bIsBrand = bId === 'pf_v_brand' || bId === 'pf_v_vendor';
        if (aIsBrand && !bIsBrand) return -1;
        if (!aIsBrand && bIsBrand) return 1;

        // Colour always last
        const aIsColour = aId === 'pf_opt_color' || aId === 'pf_opt_colour';
        const bIsColour = bId === 'pf_opt_color' || bId === 'pf_opt_colour';
        if (aIsColour && !bIsColour) return 1;
        if (!aIsColour && bIsColour) return -1;

        // Everything else alphabetically by label
        return aLabel.localeCompare(bLabel);
      });

      // Store filters for later use
      this.boostFilters = {};
      activeFilters.forEach(f => {
        this.boostFilters[f.filterOptionId] = f;
      });

      // Store enabled filter keys
      this.enabledFilterKeys = activeFilters.map(f => f.filterOptionId);
      this.state.filterVisibilitySet = true;

      // Generate HTML for each filter
      const html = activeFilters.map((filter, index) => {
        const filterKey = filter.filterOptionId;
        // Remove count from label if present (e.g., "Vendor (29)" -> "Vendor")
        let title = filter.label || this.getDefaultFilterTitle(filterKey);
        title = this.stripCountFromLabel(title);
        // Get values from different possible locations in Boost API response
        let values = filter.values || (filter.options && filter.options.values) || filter.options || [];

        // Sort values alphabetically by label
        values = [...values].sort((a, b) => {
          const labelA = (a.key || a.label || a.value || String(a)).toLowerCase();
          const labelB = (b.key || b.label || b.value || String(b)).toLowerCase();
          return labelA.localeCompare(labelB);
        });

        // Check if this is a colour filter
        const isColourFilter = this.isColourFilter(filterKey);

        const optionsHtml = values.map((value, valueIndex) => {
          // Extract label and value using helper function
          const extracted = this.extractFilterValue(value);
          const label = extracted.label;
          const filterValue = extracted.value;
          const count = extracted.count;
          const isChecked = this.isFilterValueSelected(filterKey, filterValue);

          // Get colour swatch HTML if this is a colour filter
          const swatchHtml = isColourFilter ? this.getColourSwatchHtml(label) : '';

          return `
            <li class="tag${isChecked ? ' tag--active' : ''}">
              <input
                type="checkbox"
                class="no-filter"
                ${isChecked ? 'checked="checked"' : ''}
                id="${filterKey}--${valueIndex}"
                value="${this.escapeHtml(filterValue)}"
                data-boost-filter-checkbox
              />
              <label for="${filterKey}--${valueIndex}">
                ${swatchHtml}<span>${this.escapeHtml(label)}</span>
              </label>
            </li>
          `;
        }).join('');

        // Add swatches class to list if this is a colour filter
        const swatchesClass = isColourFilter ? ' tag-list--swatches' : '';

        // HTML structure matches collection-sidebar-filter-trigger.liquid and collapsible-icons.liquid
        // Accordions are collapsed by default
        return `
          <div
            class="collection-sidebar__group--auto-${index} collection-sidebar__group--block"
            data-boost-filter-group
            data-boost-filter-key="${filterKey}"
          >
            <div class="collection-sidebar__group">
              <div
                class="collection-sidebar__collapsible-trigger flex items-center justify-between py-[15px] cursor-pointer js-collapsible-trigger"
                aria-controls="auto-filter-${index}"
              >
                ${this.escapeHtml(title)}
                <span class="collapsible-trigger__icon collapsible-trigger__icon--open" role="presentation">
                  <svg class="w-[0.75rem] h-[0.438rem]" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 28 16"><path d="M1.57 1.59l12.76 12.77L27.1 1.59" stroke-width="2" stroke="#000" fill="none" fill-rule="evenodd"/></svg>
                </span>
              </div>
              <div
                id="auto-filter-${index}"
                class="collapsible-content collection-sidebar__content border-0"
              >
                <div class="collapsible-content__inner">
                  <ul class="no-bullets tag-list boost-tag-list tag-list--checkboxes${swatchesClass}" data-boost-filter-list>
                    ${optionsHtml}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        `;
      }).join('');

      autoContainer.innerHTML = html;

      // Bind collapse/expand events for auto-generated filters
      this.bindAutoFilterToggleEvents();
    },

    /**
     * Bind toggle events for auto-generated filter groups
     * Uses the same pattern as bundle.js collapsible triggers
     */
    bindAutoFilterToggleEvents: function() {
      document.querySelectorAll('[data-boost-auto-filters] .js-collapsible-trigger').forEach(trigger => {
        // Set initial collapsed state via inline styles (since we control these elements)
        const content = trigger.nextElementSibling;
        if (content) {
          content.style.height = '0';
          content.style.overflow = 'hidden';
        }

        trigger.addEventListener('click', (e) => {
          const content = trigger.nextElementSibling;
          const isOpen = trigger.classList.contains('is-open');

          if (isOpen) {
            trigger.classList.remove('is-open');
            content.classList.remove('is-open');
            content.style.height = '0';
            content.style.overflow = 'hidden';
          } else {
            trigger.classList.add('is-open');
            content.classList.add('is-open');
            content.style.height = 'auto';
            content.style.overflow = 'visible';
          }
        });
      });
    },

    /**
     * Strip count from label (e.g., "Aeyde (12)" -> "Aeyde")
     */
    stripCountFromLabel: function(label) {
      if (!label || typeof label !== 'string') return label;
      // Remove trailing " (number)" pattern
      return label.replace(/\s*\(\d+\)$/, '').trim();
    },

    /**
     * Check if a filter key is a colour/color filter
     */
    isColourFilter: function(filterKey) {
      if (!filterKey) return false;
      const key = filterKey.toLowerCase();
      return key.includes('colour') || key.includes('color');
    },

    /**
     * Get colour swatch HTML for a filter value
     * Uses shop metafields data passed via window.boostFilterConfig.colorSwatches
     */
    getColourSwatchHtml: function(colorName) {
      const swatches = window.boostFilterConfig?.colorSwatches || {};

      // Try exact match first, then case-insensitive match
      let swatch = swatches[colorName];
      if (!swatch) {
        // Case-insensitive lookup
        const colorNameLower = colorName.toLowerCase();
        const matchingKey = Object.keys(swatches).find(
          key => key.toLowerCase() === colorNameLower
        );
        if (matchingKey) {
          swatch = swatches[matchingKey];
        }
      }

      const style = [];

      if (swatch && swatch.image) {
        style.push(`background-image: url(${swatch.image})`);
      }
      if (swatch && swatch.hex) {
        style.push(`background-color: ${swatch.hex}`);
      }

      // Fallback: generate a consistent colour from the colour name if no swatch found
      if (style.length === 0) {
        const fallbackHex = this.generateColourFromName(colorName);
        style.push(`background-color: ${fallbackHex}`);
      }

      const handle = colorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      return `<span class="color-swatch color-swatch--filter color-swatch--${handle}" style="${style.join('; ')}"></span>`;
    },

    /**
     * Generate a consistent hex colour from a colour name string
     * Uses a simple hash to create reproducible colours for unknown colour names
     */
    generateColourFromName: function(name) {
      // Simple string hash
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      // Convert to HSL for better colour distribution
      // Use hue from hash, keep saturation and lightness moderate for pleasing colours
      const hue = Math.abs(hash) % 360;
      const saturation = 45 + (Math.abs(hash >> 8) % 25); // 45-70%
      const lightness = 50 + (Math.abs(hash >> 16) % 15); // 50-65%

      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    },

    /**
     * Extract label, value, and count from a filter value object
     * Boost API format: { key: "Paris Georgia", doc_count: 24 }
     */
    extractFilterValue: function(value) {
      let label = '';
      let filterValue = '';
      let count = '';

      if (typeof value === 'string') {
        // Plain string - might have count appended like "Aeyde (12)"
        const countMatch = value.match(/^(.+?)\s*\((\d+)\)$/);
        if (countMatch) {
          label = countMatch[1].trim();
          filterValue = countMatch[1].trim();
          count = countMatch[2];
        } else {
          label = value;
          filterValue = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        // Boost API format: { key: "Paris Georgia", doc_count: 24 }
        // key is the primary property for both label and value
        filterValue = value.key || value.value || value.handle || value.label || '';
        label = value.label || value.title || value.name || value.key || filterValue;

        // Get count from doc_count
        count = value.doc_count || value.count || '';

        // Ensure strings
        label = String(label || '');
        filterValue = String(filterValue || '');

        // Strip count from label/value if embedded (e.g., "Aeyde (12)")
        label = this.stripCountFromLabel(label);
        filterValue = this.stripCountFromLabel(filterValue);
      }

      // Final fallbacks
      label = label || filterValue || 'Unknown';
      filterValue = filterValue || label;

      return { label, value: filterValue, count };
    },

    /**
     * Get default title for a filter key
     */
    getDefaultFilterTitle: function(filterKey) {
      const defaultTitles = {
        'pf_v_vendor': 'Brand',
        'pf_pt_product_type': 'Product Type',
        'pf_opt_size': 'Size',
        'pf_opt_colour': 'Colour',
        'pf_opt_color': 'Color',
        'pf_t_category': 'Category',
        'pf_t_clothing': 'Clothing',
        'pf_t_bags': 'Bags',
        'pf_t_shoes': 'Shoes',
        'pf_t_beachwear': 'Beachwear',
        'pf_p_price': 'Price'
      };

      if (defaultTitles[filterKey]) {
        return defaultTitles[filterKey];
      }

      // Try to generate a readable title from the key
      // pf_t_some_tag -> Some Tag
      // pf_opt_material -> Material
      let title = filterKey
        .replace(/^pf_(v_|pt_|opt_|t_|p_)/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());

      return title || filterKey;
    },

    /**
     * Parse filter tree into consistent array format
     */
    parseFilterTree: function(filterTree) {
      let filters = [];
      if (Array.isArray(filterTree)) {
        filters = filterTree;
      } else if (filterTree && typeof filterTree === 'object') {
        filters = Object.values(filterTree);
      }

      // Flatten if we have nested arrays
      if (filters.length > 0 && Array.isArray(filters[0])) {
        filters = filters.flat();
      }

      return filters;
    },

    /**
     * Show/hide filter groups based on their enabled status in Boost
     * Used in custom mode when filter blocks are configured
     */
    updateFilterVisibility: function(filterTree) {
      // Build a map of filter key -> enabled status
      const enabledFilters = {};

      // Parse filter tree
      let filters = this.parseFilterTree(filterTree);

      filters.forEach(f => {
        if (f && f.filterOptionId) {
          // Check for 'active' status (not just 'not disabled')
          enabledFilters[f.filterOptionId] = f.status === 'active';
        }
      });

      // Store the list of enabled filter keys for enforceFilterVisibility
      this.enabledFilterKeys = Object.keys(enabledFilters).filter(key => enabledFilters[key] === true);

      // Find all filter groups and show/hide based on status
      document.querySelectorAll('[data-boost-filter-key]').forEach(group => {
        const filterKey = group.dataset.boostFilterKey;
        const isEnabled = enabledFilters[filterKey];

        if (isEnabled === true) {
          // Filter is enabled in Boost - show it
          group.style.display = '';
        } else {
          // Filter is disabled OR not found in Boost - hide it
          group.style.display = 'none';
        }
      });

      // Mark that we've set filter visibility
      this.state.filterVisibilitySet = true;
    },

    /**
     * Render filter options from Boost API response into the filter containers
     * Used in custom mode when filter blocks are configured in theme editor
     */
    renderFilterOptions: function(filterTree) {
      // Parse the filter tree
      let filters = this.parseFilterTree(filterTree);

      // Store filters for later use
      this.boostFilters = {};
      filters.forEach(f => {
        if (f && f.filterOptionId) {
          this.boostFilters[f.filterOptionId] = f;
        }
      });

      // Get custom filter configuration
      const config = this.getFilterConfig();
      const customFilters = config.customFilters || {};

      // Find all filter containers and populate them
      document.querySelectorAll('[data-boost-filter-key]').forEach(group => {
        const filterKey = group.dataset.boostFilterKey;
        const filterData = this.boostFilters[filterKey];

        if (!filterData || filterData.status !== 'active') {
          return; // Skip disabled/missing filters
        }

        // Update title if custom title is empty - use Boost's label as fallback
        const customTitle = group.dataset.boostCustomTitle;
        if (!customTitle || customTitle.trim() === '') {
          let boostLabel = filterData.label || this.getDefaultFilterTitle(filterKey);
          // Strip count from label if present (e.g., "Vendor (29)" -> "Vendor")
          boostLabel = this.stripCountFromLabel(boostLabel);
          const triggerElement = group.querySelector('.collection-sidebar__collapsible-trigger');
          if (triggerElement) {
            // Get the trigger's content, preserving the collapsible icons (class is collapsible-trigger__icon)
            const iconsEl = triggerElement.querySelector('.collapsible-trigger__icon');
            if (iconsEl) {
              // Clear content and rebuild with title + icons
              triggerElement.textContent = '';
              triggerElement.appendChild(document.createTextNode(boostLabel));
              triggerElement.appendChild(iconsEl);
            } else {
              // Fallback if no icons found - look for first child text node
              const firstChild = triggerElement.firstChild;
              if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
                firstChild.textContent = boostLabel;
              } else {
                // Last resort - prepend text node
                triggerElement.insertBefore(document.createTextNode(boostLabel), triggerElement.firstChild);
              }
            }
          }
        }

        const filterList = group.querySelector('[data-boost-filter-list]');
        if (!filterList) return;

        // Get filter values - Boost API uses options.values
        let values = filterData.values || (filterData.options && filterData.options.values) || filterData.options || [];

        if (!Array.isArray(values) || values.length === 0) {
          filterList.innerHTML = '<li class="tag text-placehoder text-small">No options available</li>';
          return;
        }

        // Sort values alphabetically by label
        values = [...values].sort((a, b) => {
          const labelA = (a.key || a.label || a.value || String(a)).toLowerCase();
          const labelB = (b.key || b.label || b.value || String(b)).toLowerCase();
          return labelA.localeCompare(labelB);
        });

        // Check if this is a colour filter
        const isColourFilter = this.isColourFilter(filterKey);

        // Render filter options
        const html = values.map((value, index) => {
          // Extract label and value using helper function
          const extracted = this.extractFilterValue(value);
          const label = extracted.label;
          const filterValue = extracted.value;
          const count = extracted.count;
          const isChecked = this.isFilterValueSelected(filterKey, filterValue);

          // Get colour swatch HTML if this is a colour filter
          const swatchHtml = isColourFilter ? this.getColourSwatchHtml(label) : '';

          return `
            <li class="tag${isChecked ? ' tag--active' : ''}">
              <input
                type="checkbox"
                class="no-filter"
                ${isChecked ? 'checked="checked"' : ''}
                id="${filterKey}--${index}"
                value="${this.escapeHtml(filterValue)}"
                data-boost-filter-checkbox
              />
              <label for="${filterKey}--${index}">
                ${swatchHtml}<span>${this.escapeHtml(label)}</span>
              </label>
            </li>
          `;
        }).join('');

        // Add swatches class to list if this is a colour filter
        if (isColourFilter && filterList.classList) {
          filterList.classList.add('tag-list--swatches');
        }

        filterList.innerHTML = html;
      });
    },

    /**
     * Check if a filter value is currently selected
     */
    isFilterValueSelected: function(filterKey, value) {
      const selectedValues = this.state.currentFilters[filterKey] || [];
      return selectedValues.includes(value);
    },

    /**
     * Parse filter parameters from URL
     */
    parseUrlFilters: function() {
      const urlParams = new URLSearchParams(window.location.search);

      urlParams.forEach((value, key) => {
        if (key.startsWith('pf_')) {
          if (!this.state.currentFilters[key]) {
            this.state.currentFilters[key] = [];
          }
          // Handle multiple values for same filter
          value.split(',').forEach(v => {
            if (!this.state.currentFilters[key].includes(v)) {
              this.state.currentFilters[key].push(v);
            }
          });
        }
      });

      // Get page and sort from URL
      this.state.currentPage = parseInt(urlParams.get('page')) || 1;
      this.state.currentSort = urlParams.get('sort_by') || 'manual';
    },

    /**
     * Sync checkbox states with current filters from URL
     * This ensures checkboxes are ticked when page loads with filter params
     */
    syncCheckboxStates: function() {
      // For each filter key and its values
      Object.keys(this.state.currentFilters).forEach(filterKey => {
        const values = this.state.currentFilters[filterKey];

        // Find the filter group with this key
        const filterGroup = document.querySelector(`[data-boost-filter-key="${filterKey}"]`);
        if (!filterGroup) return;

        // For each value, find and check the matching checkbox
        values.forEach(filterValue => {
          // Find checkboxes in this group
          const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"][data-boost-filter-checkbox]');

          checkboxes.forEach(checkbox => {
            // Get the label text to compare
            const label = checkbox.nextElementSibling || document.querySelector(`label[for="${checkbox.id}"]`);
            if (!label) return;

            // Get text from the last span (for color swatches, first span is the swatch)
            const spans = label.querySelectorAll('span');
            let labelText = '';
            if (spans.length > 0) {
              labelText = spans[spans.length - 1].textContent.trim();
            } else {
              labelText = label.textContent.trim();
            }

            // Check if this checkbox matches the filter value
            if (labelText === filterValue) {
              checkbox.checked = true;
              // Also add active class to parent li if exists
              const parentLi = checkbox.closest('li.tag');
              if (parentLi) {
                parentLi.classList.add('tag--active');
              }
            }
          });
        });
      });
    },

    /**
     * Get URL parameter
     */
    getUrlParam: function(param) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param);
    },

    /**
     * Bind filter checkbox events
     */
    bindFilterEvents: function() {
      const sidebar = document.querySelector('.collection-sidebar');
      if (!sidebar) return;

      sidebar.addEventListener('change', (e) => {
        const checkbox = e.target;
        if (checkbox.type !== 'checkbox') return;

        // Ignore remove checkboxes (active filter pills)
        if (checkbox.classList.contains('tag--remove-checkbox')) return;

        // Use the checkbox value attribute directly - it's set correctly without count
        // The value attribute is set from extractFilterValue() which strips counts
        let filterValue = checkbox.value;

        // Strip any count that might have gotten in (e.g., "St. Agni (29)" -> "St. Agni")
        filterValue = this.stripCountFromLabel(filterValue);

        const filterOption = this.getFilterOptionFromCheckbox(checkbox);

        if (!filterOption) return;

        if (checkbox.checked) {
          this.addFilter(filterOption.key, filterValue);
        } else {
          this.removeFilter(filterOption.key, filterValue);
        }

        // Reset to page 1 when filter changes
        this.state.currentPage = 1;
        this.fetchFilteredProducts();
      });
    },

    /**
     * Get filter option info from checkbox
     */
    getFilterOptionFromCheckbox: function(checkbox) {
      // First, try to get the filter key from the data attribute on the group
      const blockGroup = checkbox.closest('[data-boost-filter-key]');
      if (blockGroup) {
        const key = blockGroup.dataset.boostFilterKey;
        if (key) {
          return { key, title: key };
        }
      }

      // Fallback: look in nested group structure
      const group = checkbox.closest('.collection-sidebar__group');
      if (!group) return null;

      const trigger = group.querySelector('.collection-sidebar__collapsible-trigger');
      if (!trigger) return null;

      const title = trigger.textContent.trim().toLowerCase();

      // Map filter titles to Boost pf_ keys
      // This mapping may need adjustment based on your Boost filter setup
      const filterKeyMap = {
        'colour': 'pf_opt_colour',
        'color': 'pf_opt_color',
        'size': 'pf_opt_size',
        'category': 'pf_t_category',
        'designer': 'pf_v_vendor',
        'brand': 'pf_v_vendor',
        'type': 'pf_t_product_type',
        'price': 'pf_p_price'
      };

      // Try to match or create a tag-based filter
      let key = filterKeyMap[title];
      if (!key) {
        // Default to tag filter for unknown types
        key = 'pf_t_' + title.replace(/\s+/g, '_');
      }

      return { key, title };
    },

    /**
     * Add a filter value
     */
    addFilter: function(key, value) {
      if (!this.state.currentFilters[key]) {
        this.state.currentFilters[key] = [];
      }
      if (!this.state.currentFilters[key].includes(value)) {
        this.state.currentFilters[key].push(value);
      }
    },

    /**
     * Remove a filter value
     */
    removeFilter: function(key, value) {
      if (this.state.currentFilters[key]) {
        const index = this.state.currentFilters[key].indexOf(value);
        if (index > -1) {
          this.state.currentFilters[key].splice(index, 1);
        }
        if (this.state.currentFilters[key].length === 0) {
          delete this.state.currentFilters[key];
        }
      }
    },

    /**
     * Bind sort dropdown events
     */
    bindSortEvents: function() {
      const self = this;

      // Desktop sort buttons - inside #sort-by container, buttons have data-value
      // Use capture phase and stopImmediatePropagation to intercept before bundle.js
      const sortContainer = document.querySelector('#sort-by');
      if (sortContainer) {
        sortContainer.querySelectorAll('button[data-value]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const sortValue = btn.dataset.value;
            self.state.currentSort = sortValue;
            self.state.currentPage = 1;

            // Update active state on buttons
            sortContainer.querySelectorAll('button[data-value]').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            // Always use Boost API for sorting (even without filters)
            self.fetchFilteredProducts();
          }, true); // Use capture phase
        });
      }

      // Mobile sort select - use capture phase to intercept before bundle.js
      const sortSelect = document.querySelector('#sort-by-mobile');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          self.state.currentSort = e.target.value;
          self.state.currentPage = 1;
          // Always use Boost API for sorting
          self.fetchFilteredProducts();
        }, true); // Use capture phase
      }
    },

    /**
     * Bind pagination events
     */
    bindPaginationEvents: function() {
      document.addEventListener('click', (e) => {
        const paginationLink = e.target.closest('.pagination__link, .pagination a');
        if (!paginationLink) return;

        // Intercept if we're in filtered mode OR search mode (to preserve search query)
        const hasFilters = Object.keys(this.state.currentFilters).length > 0;
        if (!hasFilters && !this.isSearchMode()) return;

        e.preventDefault();

        const href = paginationLink.getAttribute('href');
        const pageMatch = href.match(/page=(\d+)/);
        if (pageMatch) {
          this.state.currentPage = parseInt(pageMatch[1]);
        } else {
          // No page param means page 1 (first page link doesn't include page=1)
          this.state.currentPage = 1;
        }
        this.fetchFilteredProducts();
      });
    },

    /**
     * Bind clear filter events
     */
    bindClearFilterEvents: function() {
      // Clear all button - use event delegation to handle dynamically added buttons
      document.addEventListener('click', (e) => {
        const clearBtn = e.target.closest('[data-clear-filters]');
        if (clearBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          // Check if we're on mobile (inside mobile filter modal)
          const isMobile = window.innerWidth < 1024;
          this.clearAllFilters(isMobile);
        }
      }, true); // Use capture phase

      // Individual filter removal (active filter pills)
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('tag--remove-checkbox')) {
          e.preventDefault();
          const value = e.target.value;

          // Empty value means "clear all" was clicked via label
          if (!value) {
            const isMobile = window.innerWidth < 1024;
            this.clearAllFilters(isMobile);
            return;
          }

          // Remove from all filter keys
          Object.keys(this.state.currentFilters).forEach(key => {
            this.removeFilter(key, value);
          });

          this.state.currentPage = 1;

          if (Object.keys(this.state.currentFilters).length === 0) {
            // No more filters, reload page without params
            this.clearAllFilters();
          } else {
            this.fetchFilteredProducts();
          }
        }
      });
    },

    /**
     * Clear all filters
     * @param {boolean} closeMobileModal - If true, close mobile modal after clearing
     */
    clearAllFilters: function(closeMobileModal) {
      this.state.currentFilters = {};
      this.state.currentPage = 1;

      // Update URL to remove filter params
      const url = new URL(window.location.href);
      url.search = '';

      // Uncheck all filter checkboxes
      document.querySelectorAll('[data-boost-filter-checkbox]').forEach(checkbox => {
        checkbox.checked = false;
        const parentLi = checkbox.closest('li.tag');
        if (parentLi) {
          parentLi.classList.remove('tag--active');
        }
      });

      // Update URL without reload
      window.history.replaceState({}, '', url.toString());

      // Fetch unfiltered products via AJAX
      this.fetchFilteredProducts();

      // Close mobile modal if needed
      if (closeMobileModal) {
        this.closeMobileFilterModal();
      }
    },

    /**
     * Close mobile filter modal
     */
    closeMobileFilterModal: function() {
      const collection = document.querySelector('.collection');
      const filterDropdown = document.querySelector('[data-filter-dropdown]');
      const productGridWrapper = document.querySelector('[data-sidebar-product-grid]');
      const overlay = document.querySelector('.overlay');

      if (collection) {
        collection.classList.remove('filter-sidebar-is-open');
      }
      if (filterDropdown) {
        filterDropdown.classList.remove('active');
      }
      if (productGridWrapper) {
        productGridWrapper.classList.remove('grid');
      }

      // Remove body scroll lock and overlay (matches bundle.js behavior)
      document.body.classList.remove('overflow-hidden');
      if (overlay) {
        overlay.classList.remove('active');
      }

      // Also update product grid columns
      this.updateProductGridColumns();
    },

    /**
     * Bind mobile filter button events
     */
    bindMobileFilterEvents: function() {
      // "See Results" button - just closes the modal (filters already applied via AJAX)
      // Use capture phase to intercept before bundle.js handler
      const applyBtn = document.querySelector('[data-apply-filters]');
      if (applyBtn) {
        applyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.closeMobileFilterModal();
        }, true); // Use capture phase
      }
    },

    /**
     * Bind sidebar toggle events to update product grid columns
     * Also handles toggle functionality as fallback for search pages where bundle.js may not handle it
     */
    bindSidebarToggleEvents: function() {
      const isSearchPage = window.boostSearchMode === true;

      // Use event delegation to handle all toggle filter buttons
      document.addEventListener('click', (e) => {
        const toggleBtn = e.target.closest('[data-toggle-filter]');
        if (!toggleBtn) return;

        const collection = document.querySelector('.collection');
        const sidebar = document.querySelector('[data-filter-dropdown]');
        const productGridWrapper = document.querySelector('[data-sidebar-product-grid]');

        if (collection && sidebar) {
          // On collection pages, bundle.js handles the toggle - we just update grid columns
          // On search pages, bundle.js doesn't handle it, so we do the toggle ourselves
          if (isSearchPage) {
            // Check if bundle.js already handled it by checking if class changed
            const wasOpen = collection.classList.contains('filter-sidebar-is-open');

            // Use setTimeout to let any other handlers run first
            setTimeout(() => {
              const isNowOpen = collection.classList.contains('filter-sidebar-is-open');

              // If nothing toggled, we do it ourselves (search page fallback)
              if (wasOpen === isNowOpen) {
                collection.classList.toggle('filter-sidebar-is-open');
                sidebar.classList.toggle('active');

                // Toggle grid class on parent wrapper (required for CSS grid layout)
                if (productGridWrapper) {
                  productGridWrapper.classList.toggle('grid');
                }

                // Also toggle body scroll on mobile
                if (window.innerWidth < 1024) {
                  document.body.classList.toggle('overflow-hidden');
                }
              }

              this.updateProductGridColumns();
            }, 50);
          } else {
            // Collection page - just update grid columns after bundle.js handles toggle
            setTimeout(() => {
              this.updateProductGridColumns();
            }, 50);
          }
        }
      });
    },

    /**
     * Update product grid columns based on sidebar state
     * Adds/removes large-3-col class on all product items
     */
    updateProductGridColumns: function() {
      const collection = document.querySelector('.collection');
      const sidebarIsOpen = collection && collection.classList.contains('filter-sidebar-is-open');
      // Only target product items, not the no-products message
      const productItems = document.querySelectorAll('#product-grid .grid__item:not(.grid__item--no-products)');

      productItems.forEach(item => {
        if (sidebarIsOpen) {
          item.classList.add('large-3-col');
        } else {
          item.classList.remove('large-3-col');
        }
      });
    },

    /**
     * Convert internal filter key (pf_v_vendor) to Boost API parameter
     * Most filters use filterOptionId directly (pf_opt_color, pf_t_category, etc.)
     * Only vendor and product_type need special conversion
     */
    /**
     * Bind collapsible trigger events for filter accordions
     * Fallback for search pages where bundle.js may not handle these
     */
    bindCollapsibleTriggerEvents: function() {
      const isSearchPage = window.boostSearchMode === true;

      // Only add our fallback handler on search pages
      // On collection pages, bundle.js handles accordions correctly
      if (!isSearchPage) return;

      // Set initial collapsed state for Liquid-rendered custom filter accordions on search pages
      document.querySelectorAll('.collection-sidebar [data-boost-filter-key] .collapsible-content').forEach(content => {
        if (!content.classList.contains('is-open')) {
          content.style.height = '0';
          content.style.overflow = 'hidden';
        }
      });

      // Use event delegation to handle clicks on collapsible triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('.collection-sidebar .js-collapsible-trigger');
        if (!trigger) return;

        // Skip auto-generated filters - they have their own handler via bindAutoFilterToggleEvents
        if (trigger.closest('[data-boost-auto-filters]')) return;

        // Check if bundle.js already handled it by looking for height change
        const content = trigger.nextElementSibling;
        if (!content || !content.classList.contains('collapsible-content')) return;

        const wasOpen = trigger.classList.contains('is-open');

        // Use setTimeout to let bundle.js handle it first
        setTimeout(() => {
          const isNowOpen = trigger.classList.contains('is-open');

          // If bundle.js didn't toggle, we do it ourselves
          if (wasOpen === isNowOpen) {
            trigger.classList.toggle('is-open');
            content.classList.toggle('is-open');

            // Toggle height and overflow - if opening, set to auto/visible; if closing, set to 0/hidden
            if (content.classList.contains('is-open')) {
              content.style.height = 'auto';
              content.style.overflow = 'visible';
            } else {
              content.style.height = '0';
              content.style.overflow = 'hidden';
            }
          } else {
            // Bundle.js toggled the class but might not have set height/overflow
            // Ensure styles are correct
            if (content.classList.contains('is-open')) {
              if (content.style.height === '0px') content.style.height = 'auto';
              content.style.overflow = 'visible';
            } else {
              content.style.overflow = 'hidden';
            }
          }
        }, 50);
      });
    },

    convertFilterKeyToApiParam: function(key) {
      // Filter key to API parameter mapping:
      // pf_v_* -> vendor (Boost API uses "vendor" not filterOptionId)
      // pf_pt_* -> product_type (Boost API uses "product_type" not filterOptionId)
      // All other pf_* filters -> use filterOptionId directly (pf_opt_color, pf_t_*, etc.)

      if (key.startsWith('pf_v_')) {
        // Vendor filter: pf_v_vendor or pf_v_brand -> always "vendor"
        return 'vendor';
      } else if (key.startsWith('pf_pt_')) {
        // Product type filter: pf_pt_product_type -> product_type
        return 'product_type';
      }

      // Option filters (pf_opt_*), tag filters (pf_t_*), price filters (pf_p_*):
      // Use filterOptionId directly - the Boost API expects these keys as-is
      if (key.startsWith('pf_')) {
        return key;
      }

      // Fallback for non-pf_ keys
      return key;
    },

    /**
     * Build API URL with current filters
     */
    buildApiUrl: function() {
      const params = new URLSearchParams();

      // Required params
      params.set('shop', this.config.shop.domain);
      params.set('page', this.state.currentPage);
      params.set('limit', 24); // Match your product_limit
      params.set('sort', this.mapSortValue(this.state.currentSort));
      params.set('build_filter_tree', 'true');
      params.set('product_available', 'false'); // Include out-of-stock products

      // Shopify Markets parameters - required for per-country product filtering.
      // Pulled from window.Shopify (set by Shopify on every storefront page).
      params.set('return_all_currency_fields', 'false');
      if (typeof Shopify !== 'undefined') {
        if (Shopify.locale) params.set('locale', Shopify.locale);
        if (Shopify.country) params.set('country', Shopify.country);
        if (Shopify.currency) {
          if (Shopify.currency.active) params.set('currency', Shopify.currency.active);
          if (Shopify.currency.rate) params.set('currency_rate', Shopify.currency.rate);
        }
      }

      // Use OR logic for tag filters (pf_t_*) so selecting multiple categories
      // shows products matching ANY category, not ALL categories
      // Brand filters (pf_v_*) will still AND with the tag results
      params.set('tag_mode', '2');

      // Search mode: add query param, skip collection_scope
      // Collection mode: add collection_scope
      if (this.isSearchMode()) {
        params.set('q', this.getSearchTerms());
      } else {
        params.set('collection_scope', this.config.general.collection_id);
      }

      // Separate tag filters (pf_t_*) from other filters
      // Tag filters use the 'tag' parameter with tag_mode=2 for OR logic
      // Other filters (pf_v_*, pf_opt_*, etc.) use standard pf_* parameters
      const tagValues = [];

      Object.keys(this.state.currentFilters).forEach(key => {
        const values = this.state.currentFilters[key];
        if (values.length === 0) return;

        if (key.startsWith('pf_t_')) {
          // Tag filters - collect all values for the 'tag' parameter
          // This enables OR logic between all tag filter selections
          values.forEach(value => {
            tagValues.push(value);
          });
        } else {
          // Non-tag filters (vendor, options, etc.) - use standard pf_* parameters
          const apiParam = this.convertFilterKeyToApiParam(key);
          values.forEach(value => {
            params.append(apiParam + '[]', value);
          });
        }
      });

      // Add combined tag values if any exist
      if (tagValues.length > 0) {
        tagValues.forEach(value => {
          params.append('tag[]', value);
        });
      }

      return `${this.getApiBaseUrl()}?${params.toString()}`;
    },

    /**
     * Map Shopify sort values to Boost API sort values
     * Boost API accepts Shopify-style sort values with hyphens
     * Search mode uses different defaults (relevance instead of manual)
     */
    mapSortValue: function(shopifySort) {
      // Search mode sort mapping - search uses 'relevance' as default
      if (this.isSearchMode()) {
        const searchSortMap = {
          'relevance': 'relevance',
          'best-selling': 'best-selling',
          'price-ascending': 'price-ascending',
          'price-descending': 'price-descending',
          'created-ascending': 'created-ascending',
          'created-descending': 'created-descending'
        };
        return searchSortMap[shopifySort] || 'relevance';
      }

      // Collection mode sort mapping
      const sortMap = {
        'manual': 'manual',
        'best-selling': 'best-selling',
        'title-ascending': 'title-ascending',
        'title-descending': 'title-descending',
        'price-ascending': 'price-ascending',
        'price-descending': 'price-descending',
        'created-ascending': 'created-ascending',
        'created-descending': 'created-descending'
      };
      return sortMap[shopifySort] || 'manual';
    },

    /**
     * Fetch filtered products from Boost API
     */
    fetchFilteredProducts: function() {
      if (this.state.isFiltering) return;

      this.state.isFiltering = true;
      this.showLoading();
      this.updateUrl();

      const apiUrl = this.buildApiUrl();

      fetch(apiUrl)
        .then(response => response.json())
        .then(data => {
          this.state.totalProducts = data.total_product || 0;
          this.renderProducts(data.products || []);
          this.renderPagination(data.total_product || 0);
          this.updateFilterCounts(data.filter || []);
          this.updateActiveFilters();
          this.updateSearchResultsCount(data.total_product || 0);
          this.hideLoading();
          this.state.isFiltering = false;

          // Re-enforce filter visibility after products render
          // Some bundle.js or other code might be resetting filter display
          this.enforceFilterVisibility();

          // Scroll to collection nav (filter/sort buttons) so they remain visible
          // Account for fixed header that appears on scroll up (typically ~80-100px)
          const collectionNav = document.querySelector('#collection-nav') || document.querySelector('.collection__nav');
          if (collectionNav && !window.boostSdDisableScrollToTop) {
            const navRect = collectionNav.getBoundingClientRect();
            const headerHeight = document.querySelector('header')?.offsetHeight || 80;
            const offset = headerHeight + 20; // Header height + extra padding
            const scrollTarget = window.scrollY + navRect.top - offset;
            window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
          }
        })
        .catch(error => {
          console.error('BoostFilterIntegration: API error', error);
          this.hideLoading();
          this.state.isFiltering = false;
        });
    },

    /**
     * Update browser URL with current filters
     */
    updateUrl: function() {
      const url = new URL(window.location.href);

      // Clear existing filter params
      Array.from(url.searchParams.keys()).forEach(key => {
        if (key.startsWith('pf_') || key === 'page' || key === 'sort_by') {
          url.searchParams.delete(key);
        }
      });

      // Add current filters
      Object.keys(this.state.currentFilters).forEach(key => {
        const values = this.state.currentFilters[key];
        if (values.length > 0) {
          url.searchParams.set(key, values.join(','));
        }
      });

      // Add page if not 1
      if (this.state.currentPage > 1) {
        url.searchParams.set('page', this.state.currentPage);
      }

      // Add sort if not default
      if (this.state.currentSort !== 'manual') {
        url.searchParams.set('sort_by', this.state.currentSort);
      }

      window.history.replaceState({}, '', url.toString());
    },

    /**
     * Show loading state
     */
    showLoading: function() {
      const productGrid = document.querySelector('#product-grid');
      if (productGrid) {
        productGrid.classList.add('boost-loading');
        productGrid.style.opacity = '0.5';
        productGrid.style.pointerEvents = 'none';
      }
    },

    /**
     * Hide loading state
     */
    hideLoading: function() {
      const productGrid = document.querySelector('#product-grid');
      if (productGrid) {
        productGrid.classList.remove('boost-loading');
        productGrid.style.opacity = '';
        productGrid.style.pointerEvents = '';
      }
    },

    /**
     * Update search results count in heading (search page only)
     */
    updateSearchResultsCount: function(count) {
      if (!this.isSearchMode()) return;

      const countElement = document.querySelector('[data-search-count]');
      if (countElement) {
        countElement.textContent = count;
      }

      // Update the full heading if needed (for no results case)
      const headingElement = document.querySelector('[data-search-results-heading]');
      if (headingElement) {
        const terms = this.getSearchTerms();
        if (count > 0) {
          headingElement.innerHTML = `<span data-search-count>${count}</span> Results for "${terms}"`;
        } else {
          headingElement.textContent = `No Results for "${terms}"`;
        }
      }
    },

    /**
     * Render products from API response
     */
    renderProducts: function(products) {
      const productGrid = document.querySelector('#product-grid');
      if (!productGrid) return;

      if (products.length === 0) {
        productGrid.innerHTML = this.renderNoProductsMessage();
        return;
      }

      const html = products.map(product => this.renderProductCard(product)).join('');
      productGrid.innerHTML = html;

      // Re-initialize lazyload if available
      if (typeof lazySizes !== 'undefined') {
        lazySizes.init();
      }
    },

    /**
     * Get the current column layout class based on active view button
     * Returns 'large-2-col', 'large-3-col', or '' for default 4-col
     * bundle.js updates the active button when sidebar opens/closes
     */
    getCurrentViewClass: function() {
      const activeViewBtn = document.querySelector('[data-view-btn].active');
      if (activeViewBtn) {
        const value = activeViewBtn.dataset.value;
        if (value === '2') return 'large-2-col';
        if (value === '3') return 'large-3-col';
      }
      return ''; // Default 4-col layout
    },

    /**
     * Render a single product card matching the theme's product-item.liquid
     */
    renderProductCard: function(product) {
      const url = `/products/${product.handle}`;
      // Boost API returns images as 1-indexed object, check for key "2" for second image
      const hasSecondImage = product.images && (product.images[2] || product.images['2']);
      const vendor = product.vendor || '';
      const title = this.escapeHtml(product.title);

      // Boost API returns prices in dollars (not cents)
      const price = product.price_min || product.price || 0;
      const comparePrice = product.compare_at_price_min || product.compare_at_price_max || 0;
      const isOnSale = comparePrice > 0 && comparePrice > price;
      const isAvailable = product.available !== false;

      // Get images - try product.image first (featured image), then images object
      let featuredImage = '';
      let secondImage = '';

      // Primary featured image - try various properties
      featuredImage = product.image || product.featured_image || '';

      // If no featured image found, try images object (1-indexed)
      if (!featuredImage && product.images) {
        featuredImage = product.images[1] || product.images['1'] || '';
      }

      // Second image from images object
      if (product.images) {
        secondImage = product.images[2] || product.images['2'] || '';
      }

      // Calculate discount percentage
      let discountText = '';
      if (isOnSale) {
        const discountPercent = Math.round((comparePrice - price) / comparePrice * 100);
        discountText = `&nbsp;(${discountPercent}% Off)`;
      }

      // Get label tags
      let labelHtml = '';
      if (isAvailable && product.tags) {
        product.tags.forEach(tag => {
          if (tag.startsWith('label_')) {
            const labelText = tag.replace('label_', '');
            labelHtml = `<div class="pt-2.5 text-placehoder text-small">${this.escapeHtml(labelText)}</div>`;
          }
        });
      }
      if (!isAvailable) {
        labelHtml = `<div class="pt-2.5 text-placehoder text-small">${window.boostFilterSoldOutText || 'Sold Out'}</div>`;
      }

      // Price HTML
      let priceHtml = '';
      if (isOnSale) {
        priceHtml = `
          <div class="price">
            <span class="text-placehoder font-sohne line-through mr-[0.438rem]">
              ${this.formatMoney(comparePrice)}
            </span>
            <span class="price-item price-item--sale price-item--last">
              ${this.formatMoney(price)}${discountText}
            </span>
          </div>
        `;
      } else {
        priceHtml = this.formatMoney(price);
      }

      // Get current view class (large-2-col, large-3-col, or empty for 4-col)
      const viewClass = this.getCurrentViewClass();

      return `
        <li class="col-span-12 md:col-span-8 lg:col-span-6 grid__item js-product-grid-item px-2.5 sm:px-[15px] lg:px-5 ${viewClass}">
          <a href="${url}" class="no-border block ${hasSecondImage ? 'has-second-image' : ''}" title="${title}">
            <div class="image-wrapper">
              ${this.renderProductImage(featuredImage, title, 'first-image')}
              ${hasSecondImage ? this.renderProductImage(secondImage, title, 'second-image') : ''}
            </div>
            <div class="pt-2.5">
              <div class="pb-[0.313rem] text-placehoder text-medium font-ivar product-vendor">${this.escapeHtml(vendor)}</div>
              <p class="pb-2.5 text-medium text-black font-sohne product-title">${title}</p>
              <span class="block product-price">${priceHtml}</span>
              ${labelHtml}
            </div>
          </a>
        </li>
      `;
    },

    /**
     * Render product image with responsive sources
     * Matches product-item.liquid which uses heightMultiplier: 1.2
     * CSS handles the aspect ratio via --ratio-percent and object-fit: cover
     *
     * Note: first-image uses ratio-percent for aspect ratio sizing
     * second-image is position:absolute and fills the container, needs --ratio-percent:0 to prevent default
     */
    renderProductImage: function(imageSrc, alt, className) {
      // First-image needs ratio-percent for aspect ratio
      // Second-image is absolutely positioned by CSS (.has-second-image .second-image)
      const isSecondImage = className === 'second-image';

      if (!imageSrc) {
        if (isSecondImage) {
          // Don't render empty second image
          return '';
        }
        return `
          <figure class="h-full w-full ${className}" style="--ratio-percent: 150%;">
            <picture class="h-full w-full">
              <img class="w-full" src="" alt="${this.escapeHtml(alt)}" width="800" height="960" loading="lazy" style="width: 100%; height: 100%; object-fit:cover;" />
            </picture>
          </figure>
        `;
      }

      // Use width-only sizing to let Shopify CDN optimize
      const mobile = this.getImageUrl(imageSrc, '480x');
      const tablet = this.getImageUrl(imageSrc, '800x');
      const desktop = this.getImageUrl(imageSrc, '1200x');

      // For second-image, don't include --ratio-percent at all, let CSS handle positioning
      const figureStyle = isSecondImage ? '' : 'style="--ratio-percent: 150%;"';

      return `
        <figure class="h-full w-full ${className}" ${figureStyle}>
          <picture class="h-full w-full">
            <source media="(min-width: 769px)" srcset="${desktop}">
            <source media="(min-width: 461px)" srcset="${tablet}">
            <source media="(max-width: 460px)" srcset="${mobile}">
            <img
              class="w-full"
              src="${tablet}"
              width="800"
              height="960"
              loading="lazy"
              alt="${this.escapeHtml(alt)}"
              style="width: 100%; height: 100%; object-fit:cover;"
            />
          </picture>
        </figure>
      `;
    },

    /**
     * Get Shopify CDN image URL with size
     */
    getImageUrl: function(src, size) {
      if (!src) return '';

      // Handle Shopify CDN URLs
      let url = src;

      // Remove any existing size params (e.g., _100x100, _480x, etc.)
      url = url.replace(/_\d+x\d*(@\dx)?(?=\.)/, '');

      // Insert size before the file extension
      const extMatch = url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (extMatch) {
        const ext = extMatch[1];
        const query = extMatch[2] || '';
        url = url.replace(new RegExp(`\\.${ext}(\\?.*)?$`, 'i'), `_${size}.${ext}${query}`);
      }

      return url;
    },

    /**
     * Format money according to shop settings
     */
    formatMoney: function(amount) {
      const format = this.config.shop.money_format || '${{amount}}';

      // Format number with 2 decimal places, then remove trailing zeros
      let formatted = amount.toFixed(2);
      if (formatted.endsWith('.00')) {
        formatted = formatted.slice(0, -3);
      } else if (formatted.endsWith('0')) {
        formatted = formatted.slice(0, -1);
      }

      return format
        .replace('{{amount}}', formatted)
        .replace('{{amount_no_decimals}}', Math.round(amount))
        .replace('{{amount_with_comma_separator}}', formatted.replace('.', ','));
    },

    /**
     * Escape HTML entities
     */
    escapeHtml: function(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Build a pagination URL with all current state (filters, search query, sort)
     */
    buildPaginationUrl: function(page) {
      const url = new URL(window.location.href);

      // Clear existing filter and pagination params
      Array.from(url.searchParams.keys()).forEach(key => {
        if (key.startsWith('pf_') || key === 'page' || key === 'sort_by') {
          url.searchParams.delete(key);
        }
      });

      // Add current filters
      Object.keys(this.state.currentFilters).forEach(key => {
        const values = this.state.currentFilters[key];
        if (values.length > 0) {
          url.searchParams.set(key, values.join(','));
        }
      });

      // Add page
      if (page > 1) {
        url.searchParams.set('page', page);
      }

      // Add sort if not default
      if (this.state.currentSort !== 'manual') {
        url.searchParams.set('sort_by', this.state.currentSort);
      }

      return url.search || '?page=' + page;
    },

    /**
     * Render pagination for filtered results (matches theme's pagination.liquid style)
     */
    renderPagination: function(totalProducts) {
      const paginationWrapper = document.querySelector('.pagination-wrapper');
      if (!paginationWrapper) {
        // Create pagination wrapper if it doesn't exist
        const productGrid = document.querySelector('#ProductGridContainer .collection');
        if (productGrid) {
          const wrapper = document.createElement('div');
          wrapper.className = 'pagination-wrapper';
          wrapper.innerHTML = '<nav class="pagination flex items-center justify-between py-5 md-down:px-5 px-10" role="navigation"></nav>';
          productGrid.appendChild(wrapper);
        }
      }

      const paginationNav = document.querySelector('.pagination-wrapper .pagination');
      if (!paginationNav) return;

      const limit = 24;
      const totalPages = Math.ceil(totalProducts / limit);
      const currentPage = this.state.currentPage;

      if (totalPages <= 1) {
        paginationNav.closest('.pagination-wrapper').style.display = 'none';
        return;
      }

      paginationNav.closest('.pagination-wrapper').style.display = '';

      // Icon SVGs matching theme
      const caretIcon = `<svg class="" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="black"/></svg>`;
      const caretIconRotated = `<svg class="rotate-180" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="black"/></svg>`;
      const caretWithLineIcon = `<svg class="" width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="black"/><rect width="1" height="14" transform="matrix(-1 0 0 1 12 0)" fill="black"/></svg>`;
      const caretWithLineIconRotated = `<svg class="rotate-180" width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L1 13" stroke="black"/><rect width="1" height="14" transform="matrix(-1 0 0 1 12 0)" fill="black"/></svg>`;

      const prevDisabled = currentPage <= 1 ? 'pointer-events-none disabled' : '';
      const nextDisabled = currentPage >= totalPages ? 'pointer-events-none disabled' : '';

      // Build URLs that preserve all state (filters, search query, sort)
      const firstPageUrl = this.buildPaginationUrl(1);
      const prevPageUrl = this.buildPaginationUrl(currentPage - 1);
      const nextPageUrl = this.buildPaginationUrl(currentPage + 1);
      const lastPageUrl = this.buildPaginationUrl(totalPages);

      const html = `
        <div class="flex items-center pagination__left-part${prevDisabled ? ' ' + prevDisabled : ''}">
          <a href="${firstPageUrl}" class="no-border md-down:mr-5 mr-10">
            ${caretWithLineIconRotated}
          </a>
          <a href="${prevPageUrl}"
             class="${prevDisabled} pagination__item no-border pagination__item--prev pagination__item-arrow link motion-reduce flex items-center">
            ${caretIconRotated}
            <span class="pl-2.5 md-down:hidden">Previous</span>
          </a>
        </div>

        <div>
          Page ${currentPage} of ${totalPages}
        </div>

        <div class="flex items-center pagination__right-part${nextDisabled ? ' ' + nextDisabled : ''}">
          <a href="${nextPageUrl}"
             class="${nextDisabled} pagination__item no-border pagination__item--prev pagination__item-arrow link motion-reduce flex items-center md-down:mr-5 mr-10">
            <span class="pr-2.5 md-down:hidden">Next</span>
            ${caretIcon}
          </a>
          <a href="${lastPageUrl}" class="no-border">
            ${caretWithLineIcon}
          </a>
        </div>
      `;

      paginationNav.innerHTML = html;
    },

    /**
     * Update filter option counts from API response
     * Note: We do NOT update filter visibility here - that's only done from fetchFilterTree()
     * The fetchFilteredProducts response includes ALL possible filters (not just enabled ones)
     * so we can't use it to determine which filters should be shown
     */
    updateFilterCounts: function(filterTree) {
      // Store the filter tree for potential future use
      this.currentFilterTree = filterTree;

      // Update filter options with new counts from filtered results
      // But only update counts, don't change which filters are visible
      if (filterTree && this.boostFilters) {
        this.updateFilterOptionCounts(filterTree);
      }
    },

    /**
     * Update filter option counts after filtering
     * This updates the doc_count for each filter value based on filtered results
     */
    updateFilterOptionCounts: function(filterTree) {
      // Parse the filter tree
      let filters = [];
      if (Array.isArray(filterTree)) {
        filters = filterTree;
      } else if (filterTree && typeof filterTree === 'object') {
        filters = Object.values(filterTree);
      }

      if (filters.length > 0 && Array.isArray(filters[0])) {
        filters = filters.flat();
      }

      // Update each filter's counts
      filters.forEach(f => {
        if (!f || !f.filterOptionId) return;

        const filterKey = f.filterOptionId;
        const filterData = this.boostFilters[filterKey];

        // Only update if we have this filter and it has values
        if (!filterData || filterData.status !== 'active') return;

        const filterList = document.querySelector(`[data-boost-filter-key="${filterKey}"] [data-boost-filter-list]`);
        if (!filterList) return;

        // Get new values with updated counts
        const newValues = f.values || [];

        // Update existing checkboxes with new counts
        newValues.forEach((value, index) => {
          const label = value.label || value.value || value;
          const filterValue = typeof value === 'object' ? (value.value || value.label) : value;
          const count = value.doc_count || 0;
          const isChecked = this.isFilterValueSelected(filterKey, filterValue);

          // Find matching checkbox
          const checkbox = filterList.querySelector(`input[value="${this.escapeHtml(filterValue)}"]`);
          if (checkbox) {
            const labelEl = checkbox.nextElementSibling;
            if (labelEl) {
              const span = labelEl.querySelector('span');
              if (span) {
                span.textContent = `${label}${count ? ` (${count})` : ''}`;
              }
            }
            // Update checked state
            checkbox.checked = isChecked;
            const parentLi = checkbox.closest('li.tag');
            if (parentLi) {
              parentLi.classList.toggle('tag--active', isChecked);
            }
          }
        });
      });
    },

    /**
     * Render the no products found message with filter clearing options
     */
    renderNoProductsMessage: function() {
      const filterCount = Object.values(this.state.currentFilters).flat().length;

      // No filters active - just show simple message
      if (filterCount === 0) {
        return `
          <li class="col-span-24 grid__item grid__item--no-products text-center py-10">
            <p class="text-base mb-0">${window.boostFilterNoProductsText || 'No products found in this collection'}</p>
          </li>
        `;
      }

      // Build filter pills HTML
      let filterPillsHtml = '';
      Object.keys(this.state.currentFilters).forEach(key => {
        this.state.currentFilters[key].forEach((value, index) => {
          const displayValue = value.replace(/-/g, ' ');
          filterPillsHtml += `
            <li class="tag tag--remove inline-block">
              <input type="checkbox" checked="checked" class="hide tag--remove-checkbox" id="noproducts-remove--${value}--${index}" value="${value}"/>
              <label for="noproducts-remove--${value}--${index}" class="tag--remove-label p-2.5 bg-grey-light rounded-sm flex items-center leading-[1rem] cursor-pointer">
                <span class="w-2 h-2 mr-2">
                  <svg width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3536 1.35356L12.6465 0.646454L7.00004 6.2929L1.35359 0.646454L0.646484 1.35356L6.29293 7.00001L0.646484 12.6465L1.35359 13.3536L7.00004 7.70711L12.6465 13.3536L13.3536 12.6465L7.70714 7.00001L13.3536 1.35356Z" fill="currentColor"/>
                  </svg>
                </span>
                <span>${this.escapeHtml(displayValue)}</span>
              </label>
            </li>
          `;
        });
      });

      return `
        <li class="col-span-24 grid__item grid__item--no-products text-center py-10">
          <p class="text-base mb-6">${window.boostFilterNoProductsText || 'No products match your selection'}</p>
          <ul class="no-bullets tag-list tag-list--active-tags flex flex-wrap items-center justify-center gap-2 mb-4" data-no-products-filters>
            <li class="tag tag--remove tag--clear-all inline-block">
              <input type="checkbox" class="hide tag--remove-checkbox" id="noproducts-remove--all-filters" value="" data-clear-filters/>
              <label for="noproducts-remove--all-filters" class="tag--remove-label p-2.5 bg-grey-light rounded-sm flex items-center leading-[1rem] cursor-pointer">
                <span>Clear All Filters</span>
              </label>
            </li>
            ${filterPillsHtml}
          </ul>
        </li>
      `;
    },

    /**
     * Update active filter pills display
     */
    updateActiveFilters: function() {
      const activeFiltersContainer = document.querySelector('.tag-list--active-tags');
      if (!activeFiltersContainer) return;

      const filterCount = Object.values(this.state.currentFilters).flat().length;

      if (filterCount === 0) {
        activeFiltersContainer.innerHTML = '';
        return;
      }

      let html = `
        <li class="tag tag--remove tag--clear-all">
          <input type="checkbox" class="hide tag--remove-checkbox" id="remove--all-filters" value="" data-clear-filters/>
          <label for="remove--all-filters" class="tag--remove-label p-2.5 bg-grey-light rounded-sm flex items-center leading-[1rem]">
            <span>Clear All Filters</span>
          </label>
        </li>
      `;

      Object.keys(this.state.currentFilters).forEach(key => {
        this.state.currentFilters[key].forEach((value, index) => {
          const displayValue = value.replace(/-/g, ' ');
          html += `
            <li class="tag tag--remove">
              <input type="checkbox" checked="checked" class="hide tag--remove-checkbox" id="remove--${value}--${index}" value="${value}"/>
              <label for="remove--${value}--${index}" class="tag--remove-label p-2.5 bg-grey-light rounded-sm flex items-center leading-[1rem]">
                <span class="w-2 h-2 mr-2">
                  <svg width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3536 1.35356L12.6465 0.646454L7.00004 6.2929L1.35359 0.646454L0.646484 1.35356L6.29293 7.00001L0.646484 12.6465L1.35359 13.3536L7.00004 7.70711L12.6465 13.3536L13.3536 12.6465L7.70714 7.00001L13.3536 1.35356Z" fill="currentColor"/>
                  </svg>
                </span>
                <span>${this.escapeHtml(displayValue)}</span>
              </label>
            </li>
          `;
        });
      });

      activeFiltersContainer.innerHTML = html;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BoostFilterIntegration.init());
  } else {
    BoostFilterIntegration.init();
  }

  // Expose globally for debugging
  window.BoostFilterIntegration = BoostFilterIntegration;
})();
